import React, { useState, useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import tinycolor from 'tinycolor2';
import { faSun } from '@fortawesome/free-solid-svg-icons/faSun';
import { faMoon } from '@fortawesome/free-solid-svg-icons/faMoon';
import { faXmark } from '@fortawesome/free-solid-svg-icons/faXmark';
import { Icon } from '@appcues/sonar';
import { generateColor } from 'ext/lib/color';
import useClickOutside from 'ext/lib/hooks/use-click-outside';
import useToggle from 'ext/lib/hooks/use-toggle';
import {
  CurrentColor,
  Input,
  RadioButton,
  RadioButtonGroup,
} from 'ext/components/ui';
import { ColorPicker } from 'ext/components/ColorPicker';
import Tether from 'ext/components/Tether';
import { COLOR_MODES } from 'lib/user-preferences';
import { selectUserPreferences } from 'entities/user-preferences/reducer';
import { updateUserPreferences } from 'entities/user-preferences/actions';
import { DEFAULT_COLORS } from 'components/SideBarSettings/Shared/use-style-settings';
import {
  ColorPickerContainer,
  ColorPickerTitle,
  CurrentColorWrapper,
  Header,
  InputContainer,
  MainContainer,
  Group,
} from './styled';

const [LIGHT, DARK] = COLOR_MODES;

/**
 * Get conditionally alpha aware hex string from color
 *
 * @param {string} value - Color input
 * @return {string} Color hex string
 */
const getHexString = value => {
  const color = tinycolor(value);
  return color.getAlpha() === 1 ? color.toHexString() : color.toHex8String();
};

export const ColorInput = ({
  id,
  label,
  color,
  placeholder = DEFAULT_COLORS.selectedColor,
  onChange,
  colorMode,
  onUserPreferencesUpdate,
}) => {
  const $container = useRef();
  const $input = useRef();
  const $colorPicker = useRef();

  const [currentColor, setCurrentColor] = useState(color);
  const [isColorPickerVisible, toggleColorPickerVisible] = useToggle(false);

  useEffect(() => {
    setCurrentColor(color);
  }, [color]);

  useClickOutside(
    [$container, $input, $colorPicker],
    isColorPickerVisible && toggleColorPickerVisible
  );

  const onColorModeChange = value => {
    onUserPreferencesUpdate('colorMode', value);
  };

  const handleChangeComplete = value => {
    const hex = getHexString(value);
    $input.current.value = hex;
    setCurrentColor(generateColor(value));
    onChange(hex);
  };

  const handleInputChange = value => {
    // If valid color, then trigger change with hex8 value
    if (tinycolor(value).isValid()) {
      const hex = getHexString(value);
      $input.current.value = value;
      setCurrentColor(hex);
      onChange(hex);
    }
    // Or if the value is empty/cleared, return null
    else {
      $input.current.value = value;

      if (value === '') {
        onChange(null);
        setCurrentColor();
      }
    }
  };

  const handleInputBlur = value => {
    // If the value is the same as the current color, we do nothing
    if (currentColor === value) return;

    // If the value is invalid, we reset to the previous valid color
    if (!tinycolor(value).isValid() && !!currentColor) {
      $input.current.value = currentColor;
    } else {
      handleInputChange(value);
    }
  };

  const colorPicker = (
    <ColorPickerContainer role="dialog" ref={$colorPicker}>
      <Header>
        <ColorPickerTitle tabIndex="0" id="title">
          Color
        </ColorPickerTitle>

        <Group>
          <RadioButtonGroup>
            <RadioButton
              selected={colorMode === LIGHT}
              onClick={() => onColorModeChange(LIGHT)}
            >
              <Icon aria-label="Light mode" icon={faSun} size="small" />
            </RadioButton>
            <RadioButton
              selected={colorMode === DARK}
              onClick={() => onColorModeChange(DARK)}
            >
              <Icon aria-label="Dark mode" icon={faMoon} size="small" />
            </RadioButton>
          </RadioButtonGroup>

          <Icon
            aria-label="Close color picker"
            icon={faXmark}
            onClick={() => toggleColorPickerVisible(false)}
          />
        </Group>
      </Header>
      <ColorPicker
        onChangeComplete={handleChangeComplete}
        color={tinycolor(currentColor).toRgb()}
      />
    </ColorPickerContainer>
  );

  return (
    <Tether
      attachment={colorPicker}
      placement="bottom"
      visible={isColorPickerVisible}
    >
      <MainContainer ref={$container}>
        <InputContainer key={color}>
          <Input
            ref={$input}
            id={id}
            aria-label={label}
            defaultValue={color}
            placeholder={placeholder}
            onChange={({ target }) => handleInputChange(target.value)}
            onBlur={({ target }) => handleInputBlur(target.value)}
          />

          <CurrentColorWrapper>
            <CurrentColor
              aria-label="Current color"
              color={currentColor}
              onClick={() => toggleColorPickerVisible(true)}
            />
          </CurrentColorWrapper>
        </InputContainer>
      </MainContainer>
    </Tether>
  );
};

ColorInput.propTypes = {
  id: PropTypes.string,
  label: PropTypes.string,
  color: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  placeholder: PropTypes.string,
  onChange: PropTypes.func,
  colorMode: PropTypes.oneOf(COLOR_MODES),
  onUserPreferencesUpdate: PropTypes.func,
};

const mapStateToProps = state => ({
  colorMode: selectUserPreferences(state).colorMode,
});

const mapDispatchToProps = {
  onUserPreferencesUpdate: updateUserPreferences,
};

export default connect(mapStateToProps, mapDispatchToProps)(ColorInput);
